/**
 * Meldingstonen in het portaal (sessie 4, 09-09-2026).
 *
 * `/melding.wav` was een vage piep die JP structureel miste, en er was
 * maar één toon voor alles. Deze module maakt de tonen zelf met de Web
 * Audio API in plaats van een bestand te laden:
 *
 * - **hoorbaar** — een tweetoons-motief met een korte aanzet en een
 *   nette uitdemping draagt door een kamer heen, waar één sinus van
 *   50 ms dat niet doet;
 * - **herkenbaar per soort** — nieuw werk, een overdracht van Nova en
 *   een wachtende klant klinken verschillend, zodat JP zonder te
 *   kijken weet of hij moet opstaan;
 * - **geen bestand** — niets te laden, niets dat 404't na een
 *   cache-wissel, en het volume is een echte versterking in plaats van
 *   de zachte opname van weleer.
 *
 * Strak en zakelijk: rechte sinustonen in een kwint/kwart-verhouding,
 * geen melodietjes, geen speelgoedgeluid.
 *
 * Browserregel: geluid mag pas ná een gebruikersinteractie. Zolang die
 * er niet is geweest blijft de AudioContext `suspended` en valt het
 * portaal terug op de teller in de tabtitel — precies zoals eerst.
 * `meldGebruikersinteractie()` wordt bij de eerste klik/toetsaanslag
 * aangeroepen en wekt de context.
 */

export type ToonSoort = "nieuw" | "overdracht" | "klant_wacht";

/** Frequenties in Hz per soort: (aanzet, slot) en de tijd ertussen. */
const MOTIEVEN: Record<
  ToonSoort,
  { tonen: readonly number[]; stap: number; luidheid: number }
> = {
  // Neutraal en kort: er is iets nieuws binnengekomen.
  nieuw: { tonen: [880, 1174.7], stap: 0.11, luidheid: 0.9 },
  // Nova draagt over: dalend, dringender dan "nieuw".
  overdracht: { tonen: [1174.7, 880, 1174.7], stap: 0.1, luidheid: 1.0 },
  // Een klant zit te wachten: drie stijgende tonen, het luidst.
  klant_wacht: { tonen: [784, 988, 1318.5], stap: 0.13, luidheid: 1.15 },
};

/** Duur van één toon; lang genoeg om door een kamer te dragen. */
const TOON_SECONDEN = 0.18;

let context: AudioContext | null = null;
let interactieGeweest = false;

function haalContext(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    context ??= new Ctor();
    return context;
  } catch {
    // Geen Web Audio (oude browser, test-omgeving): stil overslaan.
    return null;
  }
}

/**
 * Meld dat de gebruiker iets gedaan heeft. Vanaf dat moment mag de
 * browser geluid afspelen; de AudioContext wordt hier gewekt zodat de
 * eerste melding niet alsnog stil blijft.
 */
export function meldGebruikersinteractie(): void {
  interactieGeweest = true;
  const ctx = haalContext();
  if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => undefined);
}

/** Heeft de gebruiker al iets aangeraakt? Zo niet: geen geluid. */
export function magGeluidSpelen(): boolean {
  return interactieGeweest;
}

/**
 * Speel de toon van `soort`. `volume` is 0..1 en komt van de
 * profielinstelling. Geeft `false` terug wanneer er niets klonk — dan
 * blijft de tabtitel-teller het enige signaal.
 */
export function speelToon(soort: ToonSoort, volume: number): boolean {
  if (!interactieGeweest || volume <= 0) return false;
  const ctx = haalContext();
  if (!ctx) return false;
  if (ctx.state === "suspended") {
    void ctx.resume().catch(() => undefined);
  }

  const motief = MOTIEVEN[soort];
  const start = ctx.currentTime + 0.01;
  // De piek blijft ruim onder 1 zodat gestapelde tonen niet klippen.
  const piek = Math.min(0.9, volume * motief.luidheid) * 0.35;

  motief.tonen.forEach((hz, i) => {
    const t0 = start + i * motief.stap;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(hz, t0);
    // Korte aanzet, dan uitdempen: een blokgolf-in/uit klikt hoorbaar.
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(piek, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + TOON_SECONDEN);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + TOON_SECONDEN + 0.02);
  });
  return true;
}

/** Alleen voor tests: de opgebouwde context weggooien. */
export function _resetVoorTest(): void {
  context = null;
  interactieGeweest = false;
}
